import {
  Public,
  Store,
  LocalOffer,
  AccountBox,
  History,
} from "@mui/icons-material";
import {
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { ProductDetail } from "../types";
import { useTranslation } from "../../../../hooks/useTranslation";
import { PriceHistoryDialog } from "../../../inventory/inventory/components/PriceHistory/PriceHistoryDialog";
import { useState } from "react";
import { RestrictionAction } from "../RestrictionAction";
import { getDomainByCountryCode } from "../utils";
import { ActionButton } from "../ActionButton";
import Delete from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { KeepaGraphDialog } from "../KeepaGraphDialog";
type SideBarProps = {
  productDetail?: ProductDetail;
};

const openPage = (url: string) => window.open(url, "_blank");

export const SideBar = ({ productDetail }: SideBarProps) => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [keepaOpen, setKeepaOpen] = useState(false);
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation("pages.product.sidebar");
  const navigate = useNavigate();
  if (!(productDetail?.asin && selectedCountry?.countryCode)) {
    return <CircularProgress />;
  }

  return (
    <List dense>
      {/* Amazon US */}
      <ListItemButton
        onClick={() =>
          openPage(
            `https://www.amazon.com/dp/${productDetail.asin?.toUpperCase()}`,
          )
        }
      >
        <ListItemIcon>
          <Public />
        </ListItemIcon>
        <ListItemText primary={translate("amazonViewUs")} />
      </ListItemButton>

      {/* Local Amazon */}
      <ListItemButton
        onClick={() => {
          openPage(
            `https://www.amazon.${getDomainByCountryCode(selectedCountry.countryCode)}/dp/${productDetail.asin?.toUpperCase()}`,
          );
        }}
      >
        <ListItemIcon>
          <Store />
        </ListItemIcon>
        <ListItemText
          primary={translate("amazonViewLocal", {
            localStore: selectedCountry.countryName,
          })}
        />
      </ListItemButton>

      {/* Amazon Offers */}
      <ListItemButton
        onClick={() => {
          openPage(
            `https://www.amazon.${getDomainByCountryCode(selectedCountry.countryCode)}/gp/offer-listing/${productDetail.asin?.toUpperCase()}`,
          );
        }}
      >
        <ListItemIcon>
          <LocalOffer />
        </ListItemIcon>
        <ListItemText
          primary={translate("amazonOffersLocal", {
            localStore: selectedCountry.countryName,
          })}
        />
      </ListItemButton>

      <ListItemButton
        onClick={() => {
          openPage(
            `${selectedCountry.sellerCentralURL}/myinventory/inventory?fulfilledBy=all&page=1&pageSize=25&searchField=all&searchTerm=${productDetail.asin?.toUpperCase()}&sort=date_created_desc&status=all&ref_=xx_invmgr_dnav_xx`,
          );
        }}
      >
        <ListItemIcon>
          <AccountBox />
        </ListItemIcon>
        <ListItemText primary={translate("sellerCentralView")} />
      </ListItemButton>

      <ListItemButton onClick={() => setKeepaOpen(true)}>
        <ListItemIcon>
          <img
            src="/icons/keepa.png"
            alt="Keepa"
            style={{
              width: 24,
              height: 24,
              objectFit: "contain",
            }}
          />
        </ListItemIcon>

        <ListItemText primary={translate("keepaGraphDialog.title")} />
      </ListItemButton>

      {/* Price History */}
      <ListItemButton
        onClick={() => {
          setDialogOpen(true);
        }}
      >
        <ListItemIcon>
          <History />
        </ListItemIcon>
        <ListItemText primary={translate("updateHistoryView")} />
      </ListItemButton>

      {productDetail?.asin && (
        <PriceHistoryDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
          }}
          asin={productDetail.asin}
        />
      )}

      <RestrictionAction
        title={translate("addToRestrictedProducts.title")}
        description={translate("addToRestrictedProducts.description")}
        endpoint="store/restrictions/product"
        successMessage={translate("addToRestrictedProducts.success")}
        duplicationErrorMessage={translate(
          "addToRestrictedProducts.duplicationError",
        )}
        keyword={productDetail.asin ?? ""}
      />

      <RestrictionAction
        title={translate("addToRestrictedBrands.title")}
        description={translate("addToRestrictedBrands.description")}
        endpoint="store/restrictions/brand"
        successMessage={translate("addToRestrictedBrands.success")}
        duplicationErrorMessage={translate(
          "addToRestrictedBrands.duplicationError",
        )}
        keyword={productDetail.marketplaceProduct?.brandName ?? ""}
      />

      <ActionButton
        title={translate("deleteProduct.title")}
        description={translate("deleteProduct.description")}
        endpoint={`store/${selectedCountry?.id}/product/delete`}
        method="delete"
        successMessage={translate("deleteProduct.success")}
        icon={<Delete />}
        values={{
          asinList: [productDetail.asin ?? ""],
        }}
        onSuccess={() => {
          navigate("/inventory");
        }}
      />

      <KeepaGraphDialog
        open={keepaOpen}
        onClose={() => setKeepaOpen(false)}
        asin={productDetail.asin}
      />
    </List>
  );
};
